import React, { CSSProperties } from 'react';
import { SideScroll } from '../basic';
import { SlideContainerStyled, SCBodyStyled, SCHeadingsStyled } from './SlideContainer.styles';

export type SlideStyle = 'slide' | 'cards-left' | 'cards-right';

export interface ISCProps {
  slideStyle: SlideStyle;
  openElem: number;
  transitionDuration?: number;

  elements: {
    heading: string;
    content: React.ReactNode;
  }[];
  titleExtra?: React.ReactNode;

  onHeadingClick?: (heading: number) => void;
}

export interface ISCState {
  renderedSizes: {
    width: number;
    offset: number;
  }[];
}

export class SlideContainer extends React.Component<ISCProps, ISCState> {
  private headingRefs: (HTMLSpanElement | null)[];
  private headingRef = React.createRef<HTMLDivElement>();

  constructor(props: ISCProps) {
    super(props);

    this.state = {
      renderedSizes: props.elements.map(() => ({ width: 0, offset: 0 }))
    };

    this.headingRefs = [];
  }

  private scrollToHeading = () => {
    const heading = this.headingRef.current;
    const active = this.headingRefs[this.props.openElem];
    if (heading && active) {
      active.parentElement?.scrollTo({ left: active.offsetLeft - (heading.offsetWidth - active.offsetWidth) / 2, behavior: 'smooth' });
    }
  }

  componentDidMount = () => {
    this.forceUpdate()
    setTimeout(() => this.scrollToHeading(), 100);
  };

  componentDidUpdate = (prevProps: ISCProps) => {
    if (this.props.openElem !== prevProps.openElem) {
      this.scrollToHeading()
    }
    if (prevProps.elements.some((ele, i) => ele.heading !== this.props.elements[i].heading)) {
      this.forceUpdate();
    }
  };

  public render() {
    const activeHeading = this.headingRefs[this.props.openElem];
    let activeIndicatorStyle = {} as CSSProperties;
    if (activeHeading) {
      activeIndicatorStyle = {
        left: activeHeading.offsetLeft,
        width: activeHeading.offsetWidth,
        top: activeHeading.offsetTop + activeHeading.offsetHeight - 3
      };
    }

    const ordered = <T extends any>(arr: T[]) => {
      const indexed = arr.map((item, index) => [item, index] as const);
      return this.props.slideStyle === 'cards-left' ? indexed.reverse() : indexed;
    }

    const ts = this.props.transitionDuration === undefined ? 0.4 : this.props.transitionDuration;
    return (
      <SlideContainerStyled>
        <SCHeadingsStyled ref={this.headingRef} ts={ts}>
          <SideScroll className='Scroller' sideScrollSpeed={ 10 }>
            {this.props.elements.map((elem, i) => (
              <span key={i} ref={instance => this.headingRefs[i] = instance}
                style={{ color: i === this.props.openElem ? 'white' : undefined }}
                onClick={this.props.onHeadingClick ? () => this.props.onHeadingClick!(i) : undefined}>
                {elem.heading}
              </span>
            ))}
            <div style={activeIndicatorStyle} />
          </SideScroll>
          {this.props.titleExtra ? <div className='Extra'>
            {this.props.titleExtra}
          </div> : null}
        </SCHeadingsStyled>
        
        <SCBodyStyled ts={ts}>
          {ordered(this.props.elements).map(([elem, i]) => {
            const opacity = (this.props.slideStyle === 'slide' || i === this.props.openElem) ? 100 : 0;
            const pointerEvents = i === this.props.openElem ? undefined : 'none';
            const getLeft = () => {
              if (this.props.slideStyle === 'slide')
                return i - this.props.openElem; // no stacking
              if (this.props.slideStyle === 'cards-right')
                return i <= this.props.openElem ? 0 : 1; // stack bigger to the right
              return i >= this.props.openElem ? 0 : -1; // stack smaller to the left
            }

            return <div key={i} style={{ opacity, pointerEvents, left: `${getLeft() * 100}%` }}>
              {elem.content}
            </div>
          })}
        </SCBodyStyled>
      </SlideContainerStyled>
    );
  }
}
